import React, { useCallback, useContext, useEffect, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DownloadIcon from '@mui/icons-material/Download'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ConfirmDialog from '../../../CRM/kit/ConfirmDialog'
import useDocumentPreview, { extensionOf } from '../../../components/Documents/useDocumentPreview'
import { GlobalContext } from '../../../App'
import {
  errorMessage,
  getEmployeeELearning,
  isOk,
  revokeCertificate,
  withdrawAssignment,
} from '../../helpers/trainingApi'
import { downloadCertificate } from './downloadCertificate'
import PracticalSignoffDialog from './PracticalSignoffDialog'

const ENROLMENT_STATUS = {
  inProgress: 'Working through the course',
  assessment: 'At the assessment',
  feedback: 'Passed; feedback form still to do',
  completed: 'Completed',
}

const day = (value) => (value ? moment(value).format('DD MMM YYYY') : null)

// Pass / fail and met / not met carry an icon and words, not just colour.
const ResultChip = ({ good, label }) => (
  <Chip
    size="small"
    variant="outlined"
    color={good ? 'success' : 'error'}
    icon={good ? <CheckCircleIcon /> : <CancelIcon />}
    label={label}
    sx={{ borderRadius: 1 }}
  />
)

const Fact = ({ label, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
)

// The e-learning side of one person's training: courses in progress, attempt
// history, certificates and practical sign-offs. `employeeId` is the
// Employee id (employee.employee._id on the employee page), not the user
// account id. Renders nothing when e-learning is unavailable (below Premium).
const EmployeeELearningSection = ({ employeeId, employeeName, onRecordsChanged }) => {
  const { setToast } = useContext(GlobalContext)
  const { openPreview, previewModal } = useDocumentPreview()

  const [data, setData] = useState(null)
  const [signoffTemplate, setSignoffTemplate] = useState(null)
  const [withdraw, setWithdraw] = useState(null) // enrolment
  const [revoke, setRevoke] = useState(null) // enrolment
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [dialogError, setDialogError] = useState(null)
  const [downloading, setDownloading] = useState(null)

  const load = useCallback(() => {
    if (!employeeId) return
    getEmployeeELearning(employeeId).then((res) => {
      setData(isOk(res) ? res.data : null)
    })
  }, [employeeId])

  useEffect(() => {
    load()
  }, [load])

  if (!data) return null

  const { enrolments = [], signoffs = [], signoffTemplates = [] } = data
  const inProgress = enrolments.filter((enrolment) => enrolment.status !== 'completed')
  const completed = enrolments.filter((enrolment) => enrolment.status === 'completed')

  const templateFor = (signoff) =>
    signoffTemplates.find(
      (template) =>
        String(template.courseId) === String(signoff.course) && template.pathwayId === signoff.pathwayId,
    )

  const handleDownload = async (certificateId) => {
    setDownloading(certificateId)
    const error = await downloadCertificate(certificateId)
    setDownloading(null)
    if (error) setToast({ message: error, severity: 'error' })
  }

  const handleWithdraw = async () => {
    setBusy(true)
    setDialogError(null)
    const res = await withdrawAssignment(withdraw._id)
    setBusy(false)
    if (isOk(res)) {
      setWithdraw(null)
      setToast({ message: 'Assignment withdrawn' })
      load()
    } else {
      setDialogError(errorMessage(res, 'Unable to withdraw this assignment.'))
    }
  }

  const handleRevoke = async () => {
    setBusy(true)
    setDialogError(null)
    const res = await revokeCertificate(revoke.certificate._id, reason.trim())
    setBusy(false)
    if (isOk(res)) {
      setRevoke(null)
      setToast({ message: 'Certificate withdrawn' })
      load()
      // The completion stops counting, so the records above must refresh
      onRecordsChanged?.()
    } else {
      setDialogError(errorMessage(res, 'Unable to withdraw this certificate.'))
    }
  }

  const EnrolmentCard = ({ enrolment }) => {
    const { certificate, attempts = [] } = enrolment
    const individual = enrolment.origin === 'individual'
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack gap={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
            <Box>
              <Typography fontWeight={600}>{enrolment.courseTitle ?? enrolment.catalogueCode}</Typography>
              <Typography variant="body2" color="text.secondary">
                {[enrolment.pathwayTitle, `${enrolment.catalogueCode} v${enrolment.courseVersion}`]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Box>
            <Stack direction="row" gap={1} alignItems="flex-start" flexWrap="wrap">
              {individual && (
                <Chip size="small" variant="outlined" label="Assigned individually" sx={{ borderRadius: 1 }} />
              )}
              {enrolment.testOut && (
                <Chip size="small" variant="outlined" label="Assessment first" sx={{ borderRadius: 1 }} />
              )}
            </Stack>
          </Stack>

          <Stack direction="row" columnGap={4} rowGap={1} flexWrap="wrap">
            <Fact label="Status">{ENROLMENT_STATUS[enrolment.status] ?? enrolment.status}</Fact>
            <Fact label="Started">{day(enrolment.startedAt) ?? 'Not recorded'}</Fact>
            {enrolment.status === 'completed' ? (
              <Fact label="Completed">{day(enrolment.completedAt) ?? 'Not recorded'}</Fact>
            ) : (
              <Fact label="Due">{day(enrolment.dueDate) ?? 'No due date'}</Fact>
            )}
          </Stack>

          {attempts.length > 0 && (
            <Box overflow="auto">
              <Table size="small" aria-label="Assessment attempts">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <b>Attempt</b>
                    </TableCell>
                    <TableCell>
                      <b>Date</b>
                    </TableCell>
                    <TableCell>
                      <b>Score</b>
                    </TableCell>
                    <TableCell>
                      <b>Result</b>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attempts.map((attempt) => (
                    <TableRow key={attempt._id}>
                      <TableCell>{attempt.number}</TableCell>
                      <TableCell>{moment(attempt.submittedAt).format('DD MMM YYYY, HH:mm')}</TableCell>
                      <TableCell>
                        {attempt.score}/{attempt.total}
                        {attempt.percentage != null ? ` (${Math.round(attempt.percentage)}%)` : ''}
                      </TableCell>
                      <TableCell>
                        <ResultChip good={attempt.passed} label={attempt.passed ? 'Passed' : 'Not passed'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {certificate && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
              gap={1}
              sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Certificate {certificate.verificationId}
                </Typography>
                {certificate.revokedAt ? (
                  <Typography variant="body2" color="error.main">
                    Withdrawn {day(certificate.revokedAt)}
                    {certificate.revokedReason ? `: ${certificate.revokedReason}` : ''}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {[
                      certificate.snapshot?.completedOn ? `Completed ${day(certificate.snapshot.completedOn)}` : null,
                      certificate.snapshot?.expiresOn ? `valid until ${day(certificate.snapshot.expiresOn)}` : null,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </Typography>
                )}
              </Box>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload(certificate._id)}
                  disabled={downloading === certificate._id}
                >
                  {downloading === certificate._id ? 'Preparing...' : 'Certificate'}
                </Button>
                {!certificate.revokedAt && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      setReason('')
                      setDialogError(null)
                      setRevoke(enrolment)
                    }}
                  >
                    Withdraw certificate
                  </Button>
                )}
              </Stack>
            </Stack>
          )}

          {individual && enrolment.status === 'inProgress' && attempts.length === 0 && (
            <Box>
              <Button
                size="small"
                color="error"
                onClick={() => {
                  setDialogError(null)
                  setWithdraw(enrolment)
                }}
              >
                Withdraw
              </Button>
            </Box>
          )}
        </Stack>
      </Paper>
    )
  }

  return (
    <Stack gap={2}>
      <Divider />
      <Typography variant="title3" component="h3" fontWeight={700}>
        E-learning
      </Typography>

      <Stack gap={1}>
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
          Courses in progress
        </Typography>
        {inProgress.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No e-learning courses in progress.
          </Typography>
        ) : (
          inProgress.map((enrolment) => <EnrolmentCard key={enrolment._id} enrolment={enrolment} />)
        )}
      </Stack>

      {completed.length > 0 && (
        <Stack gap={1}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
            Completed courses and certificates
          </Typography>
          {completed.map((enrolment) => (
            <EnrolmentCard key={enrolment._id} enrolment={enrolment} />
          ))}
        </Stack>
      )}

      {(signoffTemplates.length > 0 || signoffs.length > 0) && (
        <Stack gap={1}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
            Practical sign-offs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Some courses have skills that can only be shown in person. Practical sign-offs are
            recorded here by your organisation and are separate from the e-learning certificate,
            which covers the knowledge part only.
          </Typography>
          {signoffs.length > 0 && (
            <Box overflow="auto">
              <Table size="small" aria-label="Practical sign-offs">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <b>Date</b>
                    </TableCell>
                    <TableCell>
                      <b>Course</b>
                    </TableCell>
                    <TableCell>
                      <b>Assessor</b>
                    </TableCell>
                    <TableCell>
                      <b>Outcome</b>
                    </TableCell>
                    <TableCell>
                      <b>Notes</b>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {signoffs.map((signoff) => {
                    const template = templateFor(signoff)
                    const notMet = (signoff.checklist ?? []).filter((item) => !item.met)
                    return (
                      <TableRow key={signoff._id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{day(signoff.assessedAt)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{template?.courseTitle ?? signoff.catalogueCode}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {template?.pathwayTitle ?? signoff.pathwayId}
                          </Typography>
                        </TableCell>
                        <TableCell>{signoff.assessorName || 'Not recorded'}</TableCell>
                        <TableCell>
                          <ResultChip good={!!signoff.passed} label={signoff.passed ? 'All items met' : 'Not all items met'} />
                          {notMet.length > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Not met: {notMet.map((item) => item.text).join('; ')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{signoff.notes || ''}</TableCell>
                        <TableCell align="right">
                          {signoff.evidence && (
                            <Button
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() =>
                                openPreview({
                                  key: signoff.evidence,
                                  name: `Practical sign-off evidence.${extensionOf(signoff.evidence) || 'pdf'}`,
                                })
                              }
                            >
                              Evidence
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
          <Stack direction="row" gap={1} flexWrap="wrap">
            {signoffTemplates.map((template) => (
              <Button
                key={`${template.courseId}-${template.pathwayId}`}
                size="small"
                variant="outlined"
                color="third"
                startIcon={<AddIcon />}
                onClick={() => setSignoffTemplate(template)}
              >
                Record practical sign-off: {template.courseTitle}
                {template.pathwayTitle && template.pathwayTitle !== template.courseTitle
                  ? ` (${template.pathwayTitle})`
                  : ''}
              </Button>
            ))}
          </Stack>
        </Stack>
      )}

      <PracticalSignoffDialog
        open={!!signoffTemplate}
        onClose={() => setSignoffTemplate(null)}
        employeeId={employeeId}
        employeeName={employeeName}
        template={signoffTemplate}
        onSaved={() => {
          setSignoffTemplate(null)
          setToast({ message: 'Practical sign-off recorded' })
          load()
        }}
      />

      <ConfirmDialog
        open={!!withdraw}
        title="Withdraw this assignment?"
        confirmLabel="Withdraw"
        confirmColor="error"
        busy={busy}
        error={dialogError}
        onConfirm={handleWithdraw}
        onClose={() => setWithdraw(null)}
      >
        <Typography variant="body2">
          {withdraw?.courseTitle} will be removed from {employeeName ? `${employeeName}'s` : 'their'} My
          Training. This is only possible while the course has not been started.
        </Typography>
      </ConfirmDialog>

      <Dialog open={!!revoke} onClose={busy ? undefined : () => setRevoke(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Withdraw this certificate?</DialogTitle>
        <DialogContent>
          <Stack gap={2} paddingTop={1}>
            <Alert severity="warning">
              The completion stops counting towards {revoke?.courseTitle}, and anyone checking the
              certificate's verification code is told it is no longer valid. This cannot be undone.
            </Alert>
            <TextField
              label="Reason for withdrawing"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              required
              multiline
              minRows={2}
              fullWidth
              inputProps={{ maxLength: 1000 }}
              helperText="At least 5 characters. Kept on the audit trail."
            />
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevoke(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={handleRevoke}
            color="error"
            variant="contained"
            disabled={busy || reason.trim().length < 5}
          >
            Withdraw certificate
          </Button>
        </DialogActions>
      </Dialog>

      {previewModal}
    </Stack>
  )
}

export default EmployeeELearningSection
